import { actionAlert } from "@suraasa/placebo-ui"
import { useQuery } from "@tanstack/react-query"
import api from "api"
import ErrorPage from "components/ErrorPage"
import LoadingOverlay from "components/LoadingOverlay"
import PageContainer from "components/PageContainer"
import TabBar from "components/TabBar"
import useTabs, { Tabs } from "components/TabBar/hooks/useTabs"
import TopHeadingBar from "components/TopHeadingBar"
import { useSuraasaPlusNewYear } from "features/Home/SuraasaPlusNewYearOnboarding/Context"
import CpatTour from "features/Home/SuraasaPlusNewYearOnboarding/CpatTour"
import { CPAT_TOUR_TARGETS } from "features/Home/SuraasaPlusNewYearOnboarding/CpatTour/constants"
import { useGlobalContext } from "global/Context/context"
import { useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import { cpatType } from "utils/constants"
import { mapErrorsV2 } from "utils/helpers"
import { useEnrollments } from "utils/hooks/useEnrollments"

import { context } from "./context"
import CpatContactUs from "./CpatOverview/ContactUs"
import CPATDashboard from "./CpatOverview/CpatDashboard"
import CpatReport from "./CpatOverview/CpatReport"
import { NoCpatAllocatedState } from "./CpatOverview/EmptyState"

const CPATOverview = () => {
  const {
    cpat: { hasCpat, latestCPAT },
  } = useEnrollments()
  const { isSpnyPlatformIntroCompleted } = useSuraasaPlusNewYear()
  const { isNcte } = useGlobalContext()

  const tabs: Tabs = [
    {
      name: "Dashboard",
      content: () => null,
    },
    {
      name: "My Reports",
      content: () => null,
      target: CPAT_TOUR_TARGETS.reports,
    },
    // ...(throughGSG
    //   ? []
    //   : [
    //       {
    //         name: "My Reports",
    //         content: () => null,
    //         target: CPAT_TOUR_TARGETS.reports,
    //       },
    //     ]),
  ]

  const [activeTab, setActiveTab] = useTabs({
    tabs,
    initialTab: tabs[0].name,
  })

  const attempt = useQuery({
    queryKey: ["cpat-assessment", latestCPAT?.id],
    queryFn: () =>
      api.cpat.getAssessment({ params: { assessmentId: latestCPAT?.id } }),
    enabled: !!latestCPAT?.id,
  })

  useEffect(() => {
    if (attempt.isError) mapErrorsV2(attempt.error)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt.isError])

  const reports = useQuery({
    queryKey: ["cpat-reports"],
    queryFn: () =>
      api.cpat.getReports({
        queryParams: { types: cpatType },
      }),
    onError: error => console.error("Failed to fetch reports:", error),
  })

  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    const handleClose = () => {
      searchParams.delete("redirect_status")
      searchParams.delete("paymentPlanId")
      searchParams.delete("payment_intent")
      searchParams.delete("payment_intent_client_secret")
      setSearchParams(searchParams, { replace: true })

      window.location.reload()
    }

    const processStripePaymentSuccess = async () => {
      await actionAlert.success({
        title: "Payment Successful!",
        duration: 5000,
        onClose: handleClose,
      })
      handleClose()
    }

    if (searchParams.get("redirect_status") === "succeeded") {
      processStripePaymentSuccess()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!hasCpat) {
    return <NoCpatAllocatedState />
  }

  if (attempt.isLoading || reports.isLoading) {
    return <LoadingOverlay />
  }

  if (attempt.isError || reports.isError) {
    return <ErrorPage errorStatus={500} />
  }

  return (
    <context.Provider value={{ attempt: attempt, reports: reports }}>
      {isSpnyPlatformIntroCompleted && <CpatTour />}
      <div className="flex flex-col bg-white">
        <TopHeadingBar
          heading={
            isNcte ? (
              <span>National Council for Teacher Education Assessment</span>
            ) : (
              <span>Comprehensive Pedagogy Assessment of Teachers</span>
            )
          }
          renderBorder
        >
          <TabBar tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
        </TopHeadingBar>
        <PageContainer className="mt-[26px] w-full">
          {activeTab === tabs[0] && <CPATDashboard />}
          {activeTab === tabs[1] && <CpatReport />}
        </PageContainer>
        <CpatContactUs />
      </div>
    </context.Provider>
  )
}

export default CPATOverview
